<template>
  <div
    v-if="showFlash"
    :id="id"
    :class="['ds-flash', `ds-flash-${themeColor}`]"
    role="alert"
    data-testid="ds-flash"
  >
    <component :is="iconName" class="ds-flash-icon" />
    <p v-if="isContentString">{{ content }}</p>
    <DsComponentRender v-else :component="content as DsComponentRenderProp" />
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, onUpdated, ref } from "vue";
import { DsFlashNotifier, DsScroll } from "../../helpers";
import type { DsComponentRenderProp } from "../component-render";
import type { DsFlashMessage } from "./DsFlashStore";
import { useDsFlashStore } from "./DsFlashStore";
import type { DefineComponent } from "vue";

import DsComponentRender from "../component-render/DsComponentRender.vue";

import {
  DsAlertSolidIcon,
  DsCircleCheckSolidIcon,
  DsExclamationSolidIcon,
  DsInfoCircleSolidIcon,
} from "../icon";

defineOptions({
  name: "DsFlash",
});

const props = defineProps({
  id: {
    type: String,
    default: "ds-flash",
  },
  autoScroll: {
    type: Boolean,
    default: true,
  },
});

const showFlash = ref(false);
const content = ref<string | DsComponentRenderProp>("");
const themeColor = ref("");

const iconName = computed((): DefineComponent => {
  if (themeColor.value === "yellow") {
    return DsExclamationSolidIcon as unknown as DefineComponent;
  } else if (themeColor.value === "green") {
    return DsCircleCheckSolidIcon as unknown as DefineComponent;
  } else if (themeColor.value === "red") {
    return DsAlertSolidIcon as unknown as DefineComponent;
  }
  return DsInfoCircleSolidIcon as unknown as DefineComponent;
});
const isContentString = computed((): boolean => {
  return typeof content.value === "string";
});

setMessage();

const unsubscribe = useDsFlashStore().$onAction(({ name, args, after }) => {
  after(() => {
    if (name === "add" && args[0].id === props.id) {
      populateMessage(args[0]);
    }

    if (name === "remove" && args[0] === props.id) {
      showFlash.value = false;
    }
  });
});

onUpdated(() => {
  setMessage();
});

onUnmounted(() => {
  unsubscribe();
  DsFlashNotifier.hide(props.id);
});

function setMessage(): void {
  const message = useDsFlashStore().messages.find((message) => {
    return message.id === props.id;
  });
  if (message !== undefined) {
    populateMessage(message);
  }
}

function populateMessage(flash: DsFlashMessage): void {
  themeColor.value = flash.themeColor;
  content.value = flash.content;
  showFlash.value = true;
  if (props.autoScroll) {
    DsScroll.to(props.id);
  }
}

defineExpose({
  content,
  themeColor,
});
</script>

<style lang="scss" scoped>
.ds-flash {
  @apply border p-2 rounded-lg flex items-center;

  &-icon {
    @apply h-6 w-6 mr-2 flex items-center shrink-0;
  }

  &-blue {
    @apply bg-blue-50 border-blue-500 text-blue-600;
  }

  &-green {
    @apply bg-green-50 border-green-500 text-green-600;
  }

  &-yellow {
    @apply bg-yellow-50 border-yellow-500 text-yellow-600;
  }

  &-red {
    @apply bg-red-50 border-red-500 text-red-600;
  }
}
</style>
